import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import {
  RootState, setOrderItemData, useGetAudienceDiscountsQuery, useValidateTicketExclusiveMutation,
} from '../store';
import Input from '../components/atoms/Input';
import Button from '../components/atoms/Button';
import Switch from '../components/atoms/Switch';
import SectionTitle from '../components/atoms/SectionTitle';
import TextTitleCard from '../components/atoms/TitleTextCard';
import findDuplicates from '../utils/findDuplicates';
import useNavigateWithQuery from '../hooks/useNavigateWithQuery';
import { EventAudienceDiscountDTO } from '../models/event';
import RadioButton from '../components/atoms/RadioButton';
import parseErrors from '../utils/parseErrors';
import getErrorResponse from '../utils/getErrorResponse';
import getCustomTicketLabel from '../utils/getCustomTicketLabel';

const schema = yup.object({
  tickets: yup.array().of(yup.object({
    first_name: yup.string().required('Debes ingresar un nombre'),
    last_name: yup.string().required('Debes ingresar un apellido'),
    person_id: yup.string().required('Debes ingresar un DNI'),
    audience_discount_id: yup.string().optional(),
  })).required(),
});

const CompleteEntryFieldsPage: React.FC = () => {
  const navigate = useNavigateWithQuery();
  const dispatch = useDispatch();
  const selectedTickets = useSelector(
    (state: RootState) => state.tickets.ticketsSelected,
  );
  const event = useSelector((state: RootState) => state.events.selectedEvent);
  const { data: AudienceDiscounts } = useGetAudienceDiscountsQuery({
    businessId: event?.business_id || '',
    eventId: event?.id || '',
  }, { skip: !event?.business_id || !event?.id || !event?.has_audience_discounts });
  const [validateTicketExclusive, resultsValidateTicketExclusive] = useValidateTicketExclusiveMutation();
  const [customizeData, setCustomizeData] = useState(
    (event?.require_id_tickets || false) || (event?.has_audience_discounts || false),
  );
  const [audienceDiscounts, setAudienceDiscounts] = useState<{ [key: number]: EventAudienceDiscountDTO | null }>({});
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (!event) {
      navigate('../../');
    }
  }, [event]);

  if (!event) {
    return null;
  }
  const ticketItems = selectedTickets
    .map((item) => Array(item.quantity * item.ticket.items_create).fill({ ...item.ticket, seat_name: item.seat_name, seat_id: item.seat_id }))
    .flat();

  const formDefaultValues = (): {
    tickets:
    | { first_name: string; last_name: string; person_id: string }[]
    | undefined;
  } => {
    const savedData = localStorage.getItem('formItemsData');
    if (savedData) {
      return JSON.parse(savedData);
    }
    return {
      tickets: ticketItems.map(() => ({
        first_name: '',
        last_name: '',
        person_id: '',
      })),
    };
  };

  const {
    register, handleSubmit, formState, getValues, setValue, trigger,
  } = useForm<{
    tickets: Array<{
      first_name: string;
      last_name: string;
      person_id: string;
      audience_discount_id?: string;
    }>;
  }>({
    resolver: yupResolver(schema),
    defaultValues: formDefaultValues(),
  });

  const onSubmit = () => {
    const data = getValues('tickets') || [];
    const orderItemData = data.map((d, index) => ({
      ...d,
      name: d.first_name,
      ticket: ticketItems[index],
      ticket_id: ticketItems[index].id,
      ticket_extra: ticketItems[index].is_extra,
      seat_name: ticketItems[index].seat_name,
      seat_id: ticketItems[index].seat_id,
      audience_discount: audienceDiscounts[index] || undefined,
      audience_discount_id: audienceDiscounts[index]?.id,
    }));
    localStorage.setItem('formItemsData', JSON.stringify(getValues()));
    localStorage.setItem(`${event.id}_orderItemData`, JSON.stringify(orderItemData));
    dispatch(setOrderItemData(orderItemData));

    const nextPath = event.has_polls
      ? '../poll'
      : `../checkout?customized=${customizeData}`;
    navigate(nextPath, { replace: true });
  };

  const onGoBack = () => {
    localStorage.removeItem('formItemsData');
    // Navigate back without replace to maintain proper history stack
    if (event.has_numerated_seats) {
      navigate('../seats?refetch=true', { replace: true, state: { shouldRefetch: true } });
    } else {
      navigate('../tickets');
    }
  };

  const handleStateAndSubmit = async () => {
    const rawData = getValues('tickets');
    const data = Array.isArray(rawData) ? rawData : [rawData].filter(Boolean);

    if (!customizeData && data.length > 0) {
      const firstTicket = data[0];
      ticketItems.forEach((_, index) => {
        setValue(`tickets.${index}.first_name`, firstTicket?.first_name || '');
        setValue(`tickets.${index}.last_name`, firstTicket?.last_name || '');
        setValue(`tickets.${index}.person_id`, firstTicket?.person_id || '');
      });
    }

    const result = await trigger();
    if (!result) {
      return;
    }

    const duplicates = findDuplicates(data.map((d) => d.person_id));
    if (duplicates.length > 0 && event.require_id_tickets) {
      setValidationError('Los DNI no pueden repetirse');
      return;
    }

    const mustValidateTickets = event?.tickets?.some((t) => t.is_member_exclusive || !!t.event_id_exclusive);
    if (mustValidateTickets) {
      const validateMemberExclusiveResult = await validateTicketExclusive({
        businessId: event.business_id,
        eventId: event.id,
        order: {
          event_id: event.id,
          items: data.map((d, index) => ({
            person_id: d.person_id,
            ticket_id: ticketItems[index].id,
          })),
        },
      });

      if ('error' in validateMemberExclusiveResult) {
        const error = getErrorResponse(validateMemberExclusiveResult.error);
        setValidationError(parseErrors(error));
        return;
      }
    }

    handleSubmit(onSubmit)();
  };

  return (
    <div>
      {event.require_id_tickets && (
        <div className="mb-4">
          <TextTitleCard>
            <div className="text-center">
              <div className="font-medium">🚨 IMPORTANTE 🚨</div>
              <p>
                Asegurate de cargar bien el nombre, apellido y DNI de cada
                asistente. Las entradas son únicas y no se permiten datos
                duplicados.
              </p>
              <p>¡No te equivoques!</p>
              <p>
                Son <span className="font-medium">ÚNICAS</span> e{' '}
                <span className="font-medium">INTRANSFERIBLES</span>.
              </p>
            </div>
          </TextTitleCard>
        </div>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleStateAndSubmit();
        }}
      >
        <div className="glass-surface">
          <div className="flex flex-col">
            {ticketItems.map((item, index) => (
              <div key={index}>
                {(customizeData || index === 0) && (
                  <div>
                    <div className={`flex flex-col mb-4 ${index !== 0 ? 'mt-6' : ''}`}>
                      <SectionTitle
                        index={index}
                        customizeData={customizeData}
                        item={item}
                        seatName={item.seat_name}
                        event={event}
                      />
                    </div>
                    {index === 0 && !event.require_id_tickets && ticketItems.length > 1 && (
                      <div className="pl-1 mb-4">
                        <Switch
                          label={`Personalizar datos de ${getCustomTicketLabel(event, { plural: true, casing: 'lower' })}`}
                          labelClassName="text-text-primary font-medium text-sm"
                          checked={customizeData}
                          onChange={() => setCustomizeData(!customizeData)}
                          disabled={event.has_audience_discounts}
                        />
                      </div>
                    )}
                    <div className="flex flex-col gap-4">
                      <Input
                        id="first_name"
                        label="Nombre*"
                        labelAbove
                        {...register(`tickets.${index}.first_name`)}
                        errMsg={formState.errors.tickets?.[index]?.first_name?.message}
                      />
                      <Input
                        id="last_name"
                        label="Apellido*"
                        labelAbove
                        {...register(`tickets.${index}.last_name`)}
                        errMsg={
                          formState.errors.tickets?.[index]?.last_name?.message
                        }
                      />
                      <Input
                        id="person_id"
                        label="DNI*"
                        labelAbove
                        type="number"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        {...register(`tickets.${index}.person_id`)}
                        errMsg={
                          formState.errors.tickets?.[index]?.person_id?.message
                        }
                      />
                      {item.has_audience_discounts && customizeData && (
                        <div>
                          <p className='mb-2'>Descuentos por audiencia:</p>
                          <div className="flex flex-col md:flex-row gap-6">
                            {AudienceDiscounts?.data?.filter((discount) => discount.active).map((discount) => (
                              <div key={discount.id}>
                                <RadioButton
                                  name={`audience_discount_id_${index}`}
                                  value={discount.id}
                                  label={`${discount.name} - (${discount.percentage * 100}%)`}
                                  checked={audienceDiscounts[index]?.id === discount.id}
                                  checkable={true}
                                  onChange={() => {
                                    setAudienceDiscounts({
                                      ...audienceDiscounts,
                                      [index]: audienceDiscounts[index]?.id === discount.id ? null : discount,
                                    });
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                          {audienceDiscounts[index] && (
                            <p className='mt-2'>
                              *Requisito: {audienceDiscounts?.[index]?.requirements}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <span className="text-red-500 white-text-shadow">{validationError}</span>
        </div>
        <div className="flex flex-col-reverse md:flex-row gap-4 justify-center mt-4">
          <Button variant="secondary" onClick={onGoBack}>
            Volver
          </Button>
          <Button
            variant="primary"
            type="submit"
            loading={resultsValidateTicketExclusive.isLoading}
          >
            Siguiente
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CompleteEntryFieldsPage;
